/**
 * LangFeedbackToast — Cycle 43.
 *
 * Cycle 42 의 AdminConsolePage inline langFeedback state/UI 를 재사용 컴포넌트로 추출.
 * subscribeSharedLang 으로 lang 변경 자동 감지 → toast 표시 → duration 후 사라짐.
 *
 * 다른 page 가 본 컴포넌트 mount 시 자동 동일 동작 (props 0 — opt-in mount).
 *
 * 정책:
 *   - aria-live="polite" + role="status" (스크린리더 announce)
 *   - 항상 DOM 유지 (mount/unmount 보다 안정 — aria-live 표준 패턴)
 *   - fixed bottom-right + opacity transition (UX 자연)
 *   - duration default 2000ms (Cycle 42 와 동일)
 */

import { useEffect, useRef, useState } from 'react';
import { subscribeSharedLang, getSharedLang, type Lang } from '../lib/base-i18n';

interface Props {
  /** display 시간 (ms). 기본 2000. */
  duration?: number;
  /** test 격리용 — 마운트 직후 first emit 무시 (false 시 처음 lang change 도 표시). */
  skipInitial?: boolean;
}

function nativeNameFor(lang: Lang): string {
  switch (lang) {
    case 'ko': return '한국어';
    case 'en': return 'English';
    case 'ja': return '日本語';  // cycle 45
    default: return lang;
  }
}

export function LangFeedbackToast({ duration = 2000, skipInitial = true }: Props = {}) {
  const [text, setText] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 마운트 시점의 lang — subscribe 의 첫 통지가 mount 시점 통지인지 판단
  const initialLangRef = useRef<Lang>(getSharedLang());
  const sawInitialRef = useRef(false);

  useEffect(() => {
    const unsub = subscribeSharedLang(() => {
      const cur = getSharedLang();
      // skipInitial 정책 — 마운트 직후 첫 lang 그대로 무시 (동일 값 통지 시 noise 회피)
      if (skipInitial && !sawInitialRef.current && cur === initialLangRef.current) {
        sawInitialRef.current = true;
        return;
      }
      sawInitialRef.current = true;
      setText(nativeNameFor(cur));
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setText(null), duration);
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [duration, skipInitial]);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="lang-feedback-toast"
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        padding: text ? '0.5rem 1rem' : '0',
        background: text ? 'var(--color-brand, #4f46e5)' : 'transparent',
        color: '#fff',
        borderRadius: '4px',
        fontSize: '0.875rem',
        zIndex: 1100,
        pointerEvents: 'none',
        opacity: text ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      }}
    >
      {text ?? ''}
    </div>
  );
}
